/**
 * Loads the fax letters attached to an order's J-codes for the Drugs tab's document icon.
 *
 * @module useOrderFaxLetters
 */
import { useEffect, useMemo, useState } from "react";
import {
  getOrderFaxLetters,
  OrderFaxLetter,
} from "../../../../../api/postCall/faxLetter";
import { latestFaxLetterByJCodeId } from "../utils/faxLetterDocument";

export interface OrderFaxLettersState {
  byJCodeId: ReadonlyMap<string, OrderFaxLetter>;
  status: "idle" | "loading" | "ready" | "error";
}

interface FaxLettersResult {
  orderId: string;
  orgId: string;
  revision: unknown;
  letters: OrderFaxLetter[];
  status: "loading" | "ready" | "error";
}

const EMPTY_LETTERS: OrderFaxLetter[] = [];

/**
 * Fetches the order's attached fax letters on mount (and when the order changes).
 *
 * A failed or in-flight load cannot supply an exemption to the completion guard.
 * Auth-data refreshes invalidate the previous result so a newer fax or removed attachment
 * cannot inherit a cached review. Responses for a previous order/org/revision are ignored.
 *
 * @param orderId - The order shown on the Drugs tab
 * @param orgId - The caller's organization
 * @param revision - The current auth data; a refreshed response reloads the fax reviews
 * @returns The request state and newest fax per J-code
 */
export const useOrderFaxLetters = (
  orderId: string,
  orgId: string,
  revision?: unknown,
): OrderFaxLettersState => {
  const [result, setResult] = useState<FaxLettersResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!orderId || !orgId) {
      setResult(null);
      return;
    }
    const scope = { orderId, orgId, revision };
    setResult({ ...scope, letters: EMPTY_LETTERS, status: "loading" });
    getOrderFaxLetters({ order_id: orderId, org_id: orgId })
      .then((letters) => {
        if (!cancelled) setResult({ ...scope, letters, status: "ready" });
      })
      .catch(() => {
        // The API boundary logs the error; keep failure distinct from a successful empty list.
        if (!cancelled)
          setResult({ ...scope, letters: EMPTY_LETTERS, status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, orgId, revision]);

  const current =
    result?.orderId === orderId &&
    result?.orgId === orgId &&
    result?.revision === revision
      ? result
      : null;
  const letters = current?.letters ?? EMPTY_LETTERS;
  const byJCodeId = useMemo(() => latestFaxLetterByJCodeId(letters), [letters]);
  return {
    byJCodeId,
    status: !orderId || !orgId ? "idle" : (current?.status ?? "loading"),
  };
};
